import React, { useState, useEffect } from "react";
import { Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

type Frequency = "none" | "daily" | "weekly";

export const DigestSettings: React.FC = () => {
  const { user } = useAuth();
  const [frequency, setFrequency] = useState<Frequency>("none");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("profiles")
      .select("digest_frequency")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data?.digest_frequency) {
          setFrequency(data.digest_frequency as Frequency);
        }
      });
  }, [user?.id]);

  const handleChange = async (val: Frequency) => {
    if (!user?.id || saving) return;
    setSaving(true);
    setFrequency(val);
    const { error } = await supabase
      .from("profiles")
      .update({ digest_frequency: val })
      .eq("id", user.id);
    if (error) {
      toast.error("Could not save digest preference.");
    } else {
      toast.success("Digest preference saved.");
    }
    setSaving(false);
  };

  const options: { value: Frequency; label: string }[] = [
    { value: "none", label: "None" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
  ];

  return (
    <Card className="border border-[#E5E7EB]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-[#1A2E4A] flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#1A2E4A]" />
            Email Digest
          </CardTitle>
          {frequency !== "none" && (
            <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">Active</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={saving}
              onClick={() => handleChange(opt.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                frequency === opt.value
                  ? "bg-[#1A2E4A] text-white border-[#1A2E4A]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-[#1A2E4A]/50"
              } disabled:opacity-50`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Daily digest sends at 7am your time. Weekly digest sends Monday mornings.
        </p>
        <p className="text-xs text-muted-foreground">
          Email delivery coming soon — we'll notify you when your first digest is ready.
        </p>
      </CardContent>
    </Card>
  );
};

export default DigestSettings;
